import { useState, useMemo, useEffect } from 'react';
import { useParams, useRouteLoaderData } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Check as CheckIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import log from 'loglevel';

import deviceResource from '../../../../rest/resources/deviceResource';

import { useSensor } from '../../../../context/SensorContext';
import { useDeviceStatus } from '../DeviceStatusContext';

import PageSection from '../../../../components/PageSection';
import Table from '../../../../components/Table';
import Notification from '../../../../components/Notification';
import SelectInput from '../../../../components/SelectInput';

const LOGGER = log.getLogger('DeviceSampling');

const DeviceSampling = () => {
  const { id: currentDeviceId } = useParams();
  const { formatMessage, locale } = useIntl();
  const { sensors } = useSensor();
  const { isOffline } = useDeviceStatus();

  const [samplingRates, setSamplingRates] = useState(
    useRouteLoaderData('device-root').samplingRates
  );
  const [inputError, setInputError] = useState({});
  const [notification, setNotification] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const fetchedSamplingRates = await deviceResource.getDeviceSamplingRates(currentDeviceId);
        setSamplingRates(fetchedSamplingRates);
      } catch (error) {
        throw Error('Failed to get device sampling rates!');
      }
    })();
  }, [currentDeviceId]);

  const assembleMenuItems = availableSamplingRates => {
    const items = availableSamplingRates.map(freq => ({
      value: freq,
      label: formatMessage({ id: `device.management.sampling.frequencies.${freq}` }),
    }));

    items.unshift({
      label: formatMessage({ id: 'common.placeholder.not-selected' }),
      value: '',
      className: 'placeholder-menu-item',
      disabled: true,
    });

    return items;
  };

  const handleSamplingRateChange = (event, sensor) => {
    setSamplingRates(prev => ({
      ...prev,
      [sensor.name]: event.target.value,
    }));
  };

  const mapSensorsToTableRows = elements =>
    elements.map((sensor, index) => ({
      sensorName: sensor.name,
      samplingRate: (
        <SelectInput
          value={samplingRates[sensor.name] || ''}
          menuItems={assembleMenuItems(sensor.availableSamplingRates)}
          onChange={event => handleSamplingRateChange(event, sensor)}
          error={inputError[sensor.name]}
        />
      ),
      index,
    }));

  const sensorsWithSamplingRate = useMemo(
    () => mapSensorsToTableRows(sensors),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sensors, samplingRates, locale, inputError]
  );

  const headCells = [
    {
      id: 'sensorName',
      translationKey: 'device.management.sampling.sensor',
    },
    {
      id: 'samplingRate',
      translationKey: 'device.management.sampling.frequency',
    },
  ];

  const handleUpdateSamplingRate = async ({ sensorName }) => {
    const samplingRate = samplingRates[sensorName];
    if (!samplingRate) {
      setInputError({ ...inputError, [sensorName]: true });
      setNotification({
        open: true,
        severity: 'error',
        message: formatMessage({ id: 'common.required-field' }),
      });
      return;
    }

    try {
      await deviceResource.setDeviceSensorSamplingRate(currentDeviceId, {
        sensor: sensorName,
        samplingRate,
      });

      setInputError({ ...inputError, [sensorName]: false });

      setNotification({
        open: true,
        severity: 'success',
        message: formatMessage({ id: 'device.management.sampling.save.success' }),
      });
    } catch (error) {
      LOGGER.error('Failed to save sampling frequency!', error);
      setNotification({
        open: true,
        severity: 'error',
        message: formatMessage({ id: 'device.management.sampling.save.error' }),
      });
    }
  };

  const handleRefresh = async () => {
    try {
      const fetchedSamplingRates = await deviceResource.getDeviceSamplingRates(currentDeviceId);
      setSamplingRates(fetchedSamplingRates);

      setNotification({
        open: true,
        severity: 'success',
        message: formatMessage({ id: 'device.management.sampling.refresh.success' }),
      });
    } catch (error) {
      setNotification({
        open: true,
        severity: 'error',
        message: formatMessage({ id: 'device.management.sampling.refresh.error' }),
      });
    }
  };

  const headActions = [
    {
      icon: <RefreshIcon />,
      label: formatMessage({ id: 'common.button.refresh' }),
      className: 'add-button',
      onClick: handleRefresh,
    },
  ];

  const tableRowActions = [
    {
      icon: <CheckIcon />,
      onClick: handleUpdateSamplingRate,
      label: formatMessage({ id: 'common.button.save' }),
      disabled: () => isOffline,
    },
  ];

  return (
    <>
      <PageSection>
        <Table
          disablePagination
          headCells={headCells}
          headActions={headActions}
          rows={sensorsWithSamplingRate}
          actions={tableRowActions}
          defaultSort={{ orderBy: 'index' }}
        />
      </PageSection>

      <Notification
        open={notification.open}
        severity={notification.severity}
        message={notification.message}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default DeviceSampling;
